/* Product card + grid registration.
 *
 * Imported by websitehq.bundle.js BEFORE rfhq_templates.js, which reads
 * window.rfhqProductCard to mount Item Grid / Item Carousel / Product Card
 * blocks. No hooks.py change is needed — this app already ships one bundle.
 *
 * Scope, deliberately: websitehq's own blocks only (Item Grid, Item Carousel).
 * Core's /all-products and item-group listings are untouched until Phase 4.
 */

import { createModel, validateModel, assertModel, ACTION_KIND, STOCK_STATE, LAYOUT } from './model.js';
import { adaptItem, adaptItems, normalizeRoute, resolveSwatchColor, formatMoney } from './adapters/webshop.js';
import { WebshopSource, readPagination } from './adapters/webshop_source.js';
import { RecommendationSource } from './adapters/recommendations_source.js';
import { AppListingSource } from './adapters/app_listing_source.js';
import { mountAppFilters } from './filters/catalog_filters.js';
import { installWebshopGridShim } from './adapters/webshop_grid_shim.js';
import { renderCard, renderCards, replaceCard } from './card/render.js';
import { RfhqProductGrid } from './grid.js';
import { Paginator } from './paginator.js';
import { cartStore, CartStore } from './cart_store.js';
import { getSwatchColorOverrides } from './swatch_color_overrides.js';

window.rfhqProductCard = {
	// contract
	createModel,
	validateModel,
	assertModel,
	ACTION_KIND,
	STOCK_STATE,
	LAYOUT,

	// adapters — the only layer allowed to know a source's shape
	adapters: {
		webshop: {
			Source: WebshopSource,
			installGridShim: installWebshopGridShim,
			adaptItem,
			adaptItems,
			normalizeRoute,
			resolveSwatchColor,
			// Colour swatches are product-card visuals; websitehq owns them
			// outright (see variant_resolver.js's own docstring on this
			// module's scope). raftor_webhq's own legacy swatch renderers
			// (product_variant_swatches.js, pdp_variant_swatches.js --
			// surfaces this app doesn't cover, or predate this system) call
			// into this exposed pair instead of keeping their own copy, the
			// same way they already consult window.webhqOwnedSurface for
			// card ownership. Exposed even though pdp_variant_swatches.js's
			// PDP isn't a "card" at all -- the colour heuristic and the
			// override data are identical wherever they render, and
			// websitehq is the one real owner of that logic.
			getSwatchColorOverrides,
			formatMoney,
			readPagination,
		},
		// Recommendation endpoints (raftor_webhq.catalog.recommendations) - a
		// second Source alongside webshop's, for queries WebshopSource's single
		// generic listing endpoint structurally cannot serve (item-based,
		// cart-based, and personalized "for you" recommendations).
		recommendations: {
			Source: RecommendationSource,
		},
		// Any app's own product listing, named by the section (Records Source); see app_listing_source.js.
		appListing: {
			Source: AppListingSource,
		},
	},

	// renderer — pure, fetches nothing
	renderCard,
	renderCards,
	replaceCard,

	// a horizontal filter bar for a grid fed by an app's listing
	mountAppFilters,

	// grid + pagination + shared cart state
	RfhqProductGrid,
	Paginator,
	CartStore,
	cartStore,
};

// The shim must claim webshop.ProductGrid before core's ProductView constructs
// one, which happens inside frappe.ready. This module is imported by
// websitehq.bundle.js at parse time, so running it here is early enough.
// It is a no-op unless window.rfhqCatalogTakeover === true.
installWebshopGridShim();
